import { z } from "zod";

import { LocalDateSchema } from "./daily-entry";

const localTimePattern = /^([01]\d|2[0-3]):[0-5]\d$/;

export const LocalTimeSchema = z
  .string()
  .regex(localTimePattern, "Expected a time in HH:mm format.");

export const GoalSlotStatusSchema = z.enum(["DONE", "DUE_LATER", "MISSED", "SKIPPED"]);

export const GoalCompletionKindSchema = z.enum(["FULL", "RECOVERY"]);

const recoveryActionSchema = z
  .string()
  .trim()
  .max(200)
  .nullable()
  .transform((value) => (value === "" ? null : value));

export const CreateDailyGoalSlotSchema = z.object({
  name: z.string().trim().min(1).max(80),
  dueTimeLocal: LocalTimeSchema,
  sortOrder: z.number().int().min(0).optional(),
  missGraceMinutes: z.number().int().min(0).max(240).default(15),
  recoveryAction: recoveryActionSchema.default(null),
  notifySelfOnOpen: z.boolean().default(false),
});

export const PatchDailyGoalSlotSchema = CreateDailyGoalSlotSchema.extend({
  id: z.string().optional(),
  active: z.boolean().default(true),
});

export const NotifyThresholdPeriodSchema = z.enum(["DAY", "WEEK"]);

export const CreateDailyGoalSchema = z.object({
  name: z.string().trim().min(1).max(100),
  sharedCircleIds: z.array(z.string()).max(10).default([]),
  notifyCircleId: z.string().nullable().default(null),
  notifyCircleOnMiss: z.boolean().default(false),
  notifyThresholdCount: z.number().int().min(1).max(20).default(1),
  notifyThresholdPeriod: NotifyThresholdPeriodSchema.default("DAY"),
  notifyDelayMinutes: z.number().int().min(0).max(1440).default(60),
  slots: z.array(CreateDailyGoalSlotSchema).min(1).max(12),
});

export const PatchDailyGoalSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    active: z.boolean().optional(),
    sharedCircleIds: z.array(z.string()).max(10).optional(),
    notifyCircleId: z.string().nullable().optional(),
    notifyCircleOnMiss: z.boolean().optional(),
    notifyThresholdCount: z.number().int().min(1).max(20).optional(),
    notifyThresholdPeriod: NotifyThresholdPeriodSchema.optional(),
    notifyDelayMinutes: z.number().int().min(0).max(1440).optional(),
    slots: z.array(PatchDailyGoalSlotSchema).min(1).max(12).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "At least one field is required.",
  });

export const DailyGoalSlotResponseSchema = z.object({
  id: z.string(),
  goalId: z.string(),
  name: z.string(),
  dueTimeLocal: LocalTimeSchema,
  sortOrder: z.number().int().min(0),
  missGraceMinutes: z.number().int().min(0),
  recoveryAction: z.string().nullable(),
  notifySelfOnOpen: z.boolean(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const DailyGoalResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  active: z.boolean(),
  sharedCircleIds: z.array(z.string()),
  notifyCircleId: z.string().nullable(),
  notifyCircleOnMiss: z.boolean(),
  notifyThresholdCount: z.number().int().min(1),
  notifyThresholdPeriod: NotifyThresholdPeriodSchema,
  notifyDelayMinutes: z.number().int().min(0),
  slots: z.array(DailyGoalSlotResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const DailyGoalsListResponseSchema = z.object({
  goals: z.array(DailyGoalResponseSchema),
});

export const DailyGoalMutationResponseSchema = z.object({
  goal: DailyGoalResponseSchema,
});

export const CompleteDailyGoalSlotSchema = z.object({
  kind: GoalCompletionKindSchema.default("FULL"),
});

export const DailyGoalCompletionResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  goalId: z.string(),
  goalSlotId: z.string(),
  localDate: LocalDateSchema,
  kind: GoalCompletionKindSchema,
  completedAt: z.string().datetime(),
});

export const DailyGoalCompletionMutationResponseSchema = z.object({
  completion: DailyGoalCompletionResponseSchema.nullable(),
});

export const DailyGoalSkipResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  goalId: z.string(),
  goalSlotId: z.string(),
  localDate: LocalDateSchema,
  skippedAt: z.string().datetime(),
});

export const DailyGoalSkipMutationResponseSchema = z.object({
  skip: DailyGoalSkipResponseSchema.nullable(),
});

export const DashboardSlotSchema = DailyGoalSlotResponseSchema.extend({
  status: GoalSlotStatusSchema,
  completedAt: z.string().datetime().nullable(),
  completionKind: GoalCompletionKindSchema.nullable(),
});

export const DashboardGoalSchema = DailyGoalResponseSchema.omit({ slots: true }).extend({
  slots: z.array(DashboardSlotSchema),
  notifyCircleName: z.string().nullable(),
});

export const TodayDashboardResponseSchema = z.object({
  localDate: LocalDateSchema,
  completedCount: z.number().int().min(0),
  totalCount: z.number().int().min(0),
  completionPercent: z.number().int().min(0).max(100),
  goals: z.array(DashboardGoalSchema),
});

export const DailyGoalHistoryQuerySchema = z
  .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
  })
  .refine((value) => value.from <= value.to, {
    message: "`from` must be less than or equal to `to`.",
    path: ["to"],
  });

export const DailyGoalHistoryDaySchema = z.object({
  localDate: LocalDateSchema,
  completedCount: z.number().int().min(0),
  totalCount: z.number().int().min(0),
  completionPercent: z.number().int().min(0).max(100),
  skippedCount: z.number().int().min(0),
});

export const DailyGoalHistoryGoalSchema = z.object({
  goalId: z.string(),
  name: z.string(),
  days: z.array(DailyGoalHistoryDaySchema),
});

export const DailyGoalHistoryResponseSchema = z.object({
  days: z.array(DailyGoalHistoryDaySchema),
  goals: z.array(DailyGoalHistoryGoalSchema),
});

export const SharedCircleGoalSlotResponseSchema = DailyGoalSlotResponseSchema.extend({
  status: GoalSlotStatusSchema,
  completedAt: z.string().datetime().nullable(),
  skippedAt: z.string().datetime().nullable(),
});

export const SharedCircleGoalResponseSchema = DailyGoalResponseSchema.omit({
  sharedCircleIds: true,
  notifyCircleId: true,
  notifyCircleOnMiss: true,
  notifyThresholdCount: true,
  notifyThresholdPeriod: true,
  notifyDelayMinutes: true,
  slots: true,
}).extend({
  sharedToCircleId: z.string(),
  ownerUserId: z.string(),
  ownerDisplayName: z.string(),
  slots: z.array(SharedCircleGoalSlotResponseSchema),
});

export const CircleMemberGoalsResponseSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  goals: z.array(SharedCircleGoalResponseSchema),
});

export const CircleProgressResponseSchema = z.object({
  members: z.array(CircleMemberGoalsResponseSchema),
});

export type LocalTime = z.infer<typeof LocalTimeSchema>;
export type GoalSlotStatus = z.infer<typeof GoalSlotStatusSchema>;
export type GoalCompletionKind = z.infer<typeof GoalCompletionKindSchema>;
export type CompleteDailyGoalSlotInput = z.infer<typeof CompleteDailyGoalSlotSchema>;
export type NotifyThresholdPeriod = z.infer<typeof NotifyThresholdPeriodSchema>;
export type DailyGoalHistoryGoal = z.infer<typeof DailyGoalHistoryGoalSchema>;
export type CreateDailyGoalSlotInput = z.infer<typeof CreateDailyGoalSlotSchema>;
export type PatchDailyGoalSlotInput = z.infer<typeof PatchDailyGoalSlotSchema>;
export type CreateDailyGoalInput = z.infer<typeof CreateDailyGoalSchema>;
export type PatchDailyGoalInput = z.infer<typeof PatchDailyGoalSchema>;
export type DailyGoalSlotResponse = z.infer<typeof DailyGoalSlotResponseSchema>;
export type DailyGoalResponse = z.infer<typeof DailyGoalResponseSchema>;
export type DailyGoalsListResponse = z.infer<typeof DailyGoalsListResponseSchema>;
export type DailyGoalCompletionResponse = z.infer<typeof DailyGoalCompletionResponseSchema>;
export type DashboardSlot = z.infer<typeof DashboardSlotSchema>;
export type DashboardGoal = z.infer<typeof DashboardGoalSchema>;
export type TodayDashboardResponse = z.infer<typeof TodayDashboardResponseSchema>;
export type DailyGoalSkipResponse = z.infer<typeof DailyGoalSkipResponseSchema>;
export type DailyGoalHistoryQuery = z.infer<typeof DailyGoalHistoryQuerySchema>;
export type DailyGoalHistoryDay = z.infer<typeof DailyGoalHistoryDaySchema>;
export type DailyGoalHistoryResponse = z.infer<typeof DailyGoalHistoryResponseSchema>;
export type SharedCircleGoalSlotResponse = z.infer<typeof SharedCircleGoalSlotResponseSchema>;
export type SharedCircleGoalResponse = z.infer<typeof SharedCircleGoalResponseSchema>;
export type CircleMemberGoalsResponse = z.infer<typeof CircleMemberGoalsResponseSchema>;
export type CircleProgressResponse = z.infer<typeof CircleProgressResponseSchema>;
